'use client'

import { useTokenHoldingsByTag } from "@/hooks/useQueryResults";
import { useMemo, useState } from "react";
import { DatasetAreaChart } from "./charts/dataset-area-chart";
import { Percent, Sigma } from "lucide-react";

interface TokenHoldingsByTagProps {
    tokenSymbol: string,
    title?: string,
    description?: string,
    dollarized?: boolean
}

export function TokenHoldingsByTag({tokenSymbol, title, description, dollarized}: TokenHoldingsByTagProps) {
    const { results } = useTokenHoldingsByTag({token: tokenSymbol})
    const [showPct, setShowPct] = useState(false)

    const formatter = new Intl.NumberFormat("en", {notation: "compact"})
    const tooltipFormatter = new Intl.NumberFormat("en", {maximumFractionDigits: 0})
    const percentFormatter = new Intl.NumberFormat("en", {style: 'percent', maximumFractionDigits: 2})

    const formattedData = useMemo(() => {
        if (!results.length) return {dimensions: [], source: []}
        const tags = [...new Set(results.map(r => r.tag))]
        const days = [...new Set(results.map(r => r.day))].sort()
        const dimensions = ['date', ...tags]
        const source = days.map(d => {
            const filtered = results.filter(r => r.day === d)
            return [
                new Date(d),
                ...tags.map(t => {
                    const point = filtered.find(r => r.tag === t)
                    return point ? Number(point.balance) : 0
                }) as number[]
            ] as (Date | number)[]
        })

        return {
            dimensions,
            source
        }
    }, [results])

    const pctData = useMemo(() => {
        return {
            dimensions: formattedData.dimensions,
            source: formattedData.source.map(s => {
                const sum = s.slice(1).reduce((a, i) => (a as number) + (i as number), 0) as number
                return [s[0], ...s.slice(1).map(v => (v as number)/sum)]
            })
        }
    }, [formattedData])

    const _switch = () => setShowPct(s => !s)

    return <div className="w-full h-[40vh]">
        <DatasetAreaChart
            dataset={showPct?pctData:formattedData}
            title={title || `${tokenSymbol} holdings by tag`}
            description={description}
            stack
            yAxisFormatter={(n) => showPct?percentFormatter.format(n):formatter.format(n)}
            tooltip={{
                seriesValueFormatter: (value) => showPct?percentFormatter.format(value):`${dollarized?'$':''}${tooltipFormatter.format(value)} ${dollarized?'':tokenSymbol}`
            }}
            tooltipTopN={15}
            showTotal={!showPct}
            legendActions={[
                {
                    name: `Switch to ${showPct?'nominal':'percent'} view`,
                    action: _switch,
                    icon: showPct?Sigma:Percent
                }
            ]}
        />
    </div>
}

export function SkyTokenHoldingsByTag() {
    return <TokenHoldingsByTag tokenSymbol="SKY" />
}

export function UsdsTokenHoldingsByTag() {
    return <TokenHoldingsByTag tokenSymbol="USDS" />
}

export function UsdeTokenHoldingsByTag() {
    return <TokenHoldingsByTag tokenSymbol="USDE" />
}

export function sUsdsTokenHoldingsByTag() {
    return <TokenHoldingsByTag tokenSymbol="sUSDS" />
}

export function sUsdeTokenHoldingsByTag() {
    return <TokenHoldingsByTag tokenSymbol="sUSDE" />
}

export function spUsdcTokenHoldingsByTag() {
    return <TokenHoldingsByTag tokenSymbol="spUSDC" />
}

export function spUsdtTokenHoldingsByTag() {
    return <TokenHoldingsByTag tokenSymbol="spUSDT" />
}

